import { ChevronDown, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/utils';

export type EnrollmentBookingStatus = 'pending' | 'confirmed';

const CHIP_BASE =
  'inline-flex h-6 shrink-0 items-center justify-center gap-0.5 rounded px-1.5 text-[10px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const STATUS_CHIP: Record<
  EnrollmentBookingStatus,
  { label: string; className: string }
> = {
  pending: {
    label: 'Unconfirmed',
    className:
      'bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/50 dark:text-amber-100 dark:hover:bg-amber-900/70',
  },
  confirmed: {
    label: 'Confirmed',
    className:
      'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-200 dark:hover:bg-green-900/55',
  },
};

export interface EnrollmentBookingStatusChipProps {
  bookingId: string;
  status: EnrollmentBookingStatus;
  updatingBookingId: string | null;
  disabled?: boolean;
  onStatusChange: (bookingId: string, status: EnrollmentBookingStatus) => void;
}

export function EnrollmentBookingStatusChip({
  bookingId,
  status,
  updatingBookingId,
  disabled = false,
  onStatusChange,
}: EnrollmentBookingStatusChipProps) {
  const isLoading = updatingBookingId === bookingId;
  const anotherInFlight = updatingBookingId != null && !isLoading;
  const isDisabled = disabled || anotherInFlight || isLoading;
  const current = STATUS_CHIP[status];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Change booking status"
          aria-label={`Booking status: ${current.label}. Click to change.`}
          disabled={isDisabled}
          className={cn(
            CHIP_BASE,
            'w-[5rem] sm:w-[5.75rem]',
            current.className
          )}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <>
              <span>{current.label}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        sideOffset={4}
        className="min-w-[5rem] sm:min-w-[5.75rem] p-1"
      >
        {(Object.keys(STATUS_CHIP) as EnrollmentBookingStatus[]).map((option) => {
          const chip = STATUS_CHIP[option];
          const isActive = option === status;
          return (
            <DropdownMenuItem
              key={option}
              disabled={isActive}
              onSelect={() => onStatusChange(bookingId, option)}
              className="cursor-pointer justify-center p-0 focus:bg-transparent"
            >
              <span
                className={cn(
                  CHIP_BASE,
                  'w-[5rem] sm:w-[5.75rem]',
                  chip.className,
                  isActive && 'ring-1 ring-inset ring-black/10 dark:ring-white/20'
                )}
              >
                {chip.label}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
